import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { appraisalState } from "./appraisalSlice";
import GridTable from "../../components/GridTable";
import { showNotification } from "../../components/Notification";
import Dropdown from "../../components/Dropdown";
import { allUsers } from "../user/userSlice";
import { getTemplates } from "../template/templateSlice";
import { formatDateToDDMMYYYY } from "../../uitls/Utils";
import Swal from "sweetalert2";
import { deleteAppraisal, getAllAppraisal } from "../../api/appraisalApi";
import { getAllUsers } from "../../api/userApi";
import { getAllTemplates } from "../../api/templateApi";
import { useNavigate } from "react-router-dom";

const ReviewAppraisal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const appraisalData = useSelector(appraisalState);
  const users = useSelector(allUsers);
  const templates = useSelector(getTemplates);

  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  useEffect(() => {
    dispatch(getAllAppraisal());
    dispatch(getAllUsers());
    dispatch(getAllTemplates());
  }, [dispatch]);

  // Filter appraisals directly from Redux state, sorted by createdDate descending
  const filteredAppraisals = React.useMemo(() => {
    if (!appraisalData?.appraisals) return [];

    let filtered = appraisalData.appraisals;

    if (selectedEmployee) {
      filtered = filtered.filter(
        (appraisal) => appraisal.employeeId === selectedEmployee
      );
    }

    if (selectedTemplate) {
      filtered = filtered.filter(
        (appraisal) => appraisal.templateId === selectedTemplate
      );
    }

    if (selectedStatus) {
      filtered = filtered.filter(
        (appraisal) => appraisal.status === selectedStatus
      );
    }

    // Sort by createdDate descending (newest first)
    return filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt || a.createdDate || 0);
      const dateB = new Date(b.createdAt || b.createdDate || 0);
      return dateB - dateA; // Descending order (newest first)
    });
  }, [
    appraisalData?.appraisals,
    selectedEmployee,
    selectedTemplate,
    selectedStatus,
  ]);

  const handleEdit = useCallback((appraisal) => {
    navigate(`/mgr/update-appraisals`, { state: { appraisal } });
  }, [navigate]);

  const handleDelete = useCallback(async (appraisalId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
  try {
    await dispatch(deleteAppraisal(appraisalId)).unwrap();
    showNotification("success", "Appraisal deleted successfully");
  } catch (error) {
    const message = error?.message || `Delete failed: ${error.status}`;
    showNotification("error", message);
  }
    }
  }, []);

  const getEmployeeName = useCallback((employeeId) => {
    const employee = users?.find((user) => user.id === employeeId);
    return employee ? `${employee.firstName} ${employee.lastName}` : "Unknown";
  }, [users]);

  const getTemplateName = useCallback((templateId) => {
    const template = templates?.find((temp) => temp.id === templateId);
    return template ? template.templateName : "Unknown";
  }, [templates]);

  const getStatusColor = useCallback((status) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "in_progress":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  }, []);

  const columns = useMemo(() => [
    {
      field: "employeeName",
      headerName: "Employee",
      width: 200,
      renderCell: (params) => getEmployeeName(params.row.employeeId),
    },
    {
      field: "templateName",
      headerName: "Template",
      width: 200,
      renderCell: (params) => getTemplateName(params.row.templateId),
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
            params.row.status
          )}`}
        >
          {params.row.status}
        </span>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created Date",
      width: 150,
      renderCell: (params) => formatDateToDDMMYYYY(params.row.createdAt),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleEdit(params.row)}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            Edit
          </button>
          <button
            onClick={() => handleDelete(params.row.id)}
            className="text-red-600 hover:text-red-800 text-sm"
          >
            Delete
          </button>
        </div>
      ),
    },
  ], [handleEdit, handleDelete, getEmployeeName, getTemplateName, getStatusColor]);

  const employeeOptions = useMemo(() => users?.map((user) => ({
    value: user.id,
    label: `${user.firstName} ${user.lastName}`,
  })) || [], [users]);

  const templateOptions = useMemo(() => templates?.map((template) => ({
    value: template.id,
    label: template.templateName,
  })) || [], [templates]);

  const statusOptions = useMemo(() => [
    { value: "pending", label: "Pending" },
    { value: "in_progress", label: "In Progress" },
    { value: "completed", label: "Completed" },
  ], []);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Review Appraisals
        </h1>
        <p className="text-gray-600">Review and manage employee appraisals</p>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label
              htmlFor="filter-employee"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Filter by Employee
            </label>
            <Dropdown
              id="filter-employee"
              options={employeeOptions}
              value={selectedEmployee}
              onChange={setSelectedEmployee}
              placeholder="Select Employee"
            />
          </div>
          <div>
            <label
              htmlFor="filter-template"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Filter by Template
            </label>
            <Dropdown
              id="filter-template"
              options={templateOptions}
              value={selectedTemplate}
              onChange={setSelectedTemplate}
              placeholder="Select Template"
            />
          </div>
          <div>
            <label
              htmlFor="filter-status"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Filter by Status
            </label>
            <Dropdown
              id="filter-status"
              options={statusOptions}
              value={selectedStatus}
              onChange={setSelectedStatus}
              placeholder="Select Status"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <GridTable
          data={filteredAppraisals}
          columns={columns}
          loading={appraisalData?.loading}
        />
      </div>
    </div>
  );
};

export { ReviewAppraisal };
export default ReviewAppraisal;
